import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, FileText, Plus } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { apiClient, getApiErrorMessage } from "@/lib/api";
import { SECTIONS } from "@/lib/sections";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    apiClient.get("/admin/stats")
      .then(({ data }) => setStats(data))
      .catch((e) => toast.error(getApiErrorMessage(e)));
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-chivo text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500">
            {stats ? `${stats.published_reports} published of ${stats.total_reports} total reports` : "Loading..."}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid={ADMIN.quickPublishButton} className="bg-gray-900 hover:bg-gray-800">
              <Plus className="h-4 w-4 mr-1.5" /> Quick Publish
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {SECTIONS.map((s) => (
              <DropdownMenuItem key={s.key} data-testid={`admin-quick-publish-${s.key}`}
                onClick={() => navigate(`/admin/section/${s.key}?new=1`)} className="cursor-pointer">
                <s.icon className="h-4 w-4 mr-2 text-gray-500" /> {s.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div data-testid={ADMIN.dashboardStats} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {SECTIONS.map((s) => {
          const sec = stats?.sections?.[s.key];
          return (
            <Link
              key={s.key}
              data-testid={`${ADMIN.sectionTile}-${s.key}`}
              to={`/admin/section/${s.key}`}
              className="group rounded-lg border border-gray-200 bg-white p-4 hover:border-gray-400 transition-colors"
            >
              <div className="flex items-center gap-2 text-gray-500 text-xs font-medium mb-3">
                <s.icon className="h-3.5 w-3.5" /> {s.label}
              </div>
              <p className="font-chivo text-2xl font-bold text-gray-900">{sec?.published ?? "—"}</p>
              <p className="text-xs text-gray-400 mt-0.5">{sec ? `${sec.total} total (incl. drafts)` : ""}</p>
              <p className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 group-hover:text-gray-900 mt-3">
                Publish here <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
              </p>
            </Link>
          );
        })}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Recently updated</h2>
        </div>
        {stats && stats.recent_reports.length === 0 && (
          <div className="py-14 text-center">
            <FileText className="h-6 w-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No reports yet. Open a section and publish your first KIRA Score.</p>
          </div>
        )}
        {stats?.recent_reports.map((r) => (
          <Link key={r.id} to={`/admin/section/${r.section}`}
            className="flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50">
            <div>
              <p className="text-sm font-medium text-gray-900">{r.company_name}</p>
              <p className="text-xs text-gray-500 font-mono">{r.sector} · {r.report_date}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-gray-600">KIRA {r.kira_score}</span>
              <span className={`rounded px-2 py-0.5 text-[11px] font-medium capitalize ${r.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                {r.status}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
